import { Analytics } from '@analytics';
import { Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MoreHoriz as MoreHorizIcon } from '@material-ui/icons';
import { regionalURLs } from '@utils/constants';
import { generateBusinessCardPrintout } from '@utils/printing_templates/generateBusinessCardPrintout';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AccessControlledButton from '../AccessControlledButton';
import AddToursModal from '../AddToursModal';
import ReviewFirstModal from '../ReviewFirstModal';
import messages from './messages';

const useStyles = makeStyles({
  menu: {
    '& .MuiPaper-root': {
      width: '240px',
      marginTop: '8px',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.1)'
    },
    '& .MuiList-root': {
      padding: '0 !important'
    },

    // Keep the dropdown compact: at 71px per row the menu outgrew the space
    // under its trigger and Material-UI flipped it above/away from the button.
    '& .MuiButtonBase-root': {
      fontSize: '15px',
      justifyContent: 'center',
      height: '48px',
      color: '#475569',
      fontWeight: '600',

      '&:hover': {
        backgroundColor: '#f8fafc'
      }
    }
  }
});

const AgentProfileSummaryButton = ({
  activeTours,
  profile,
  addToursToProfile,
  userId,
  handleQRDownload,
  setShouldShowCopyAlert,
  companyDirectToPostTip,
  setGuideReviewFirst
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isReviewFirstOpen, setIsReviewFirstOpen] = useState(false);
  const classes = useStyles();
  const intl = useIntl();

  const linkUrl = (regionalURLs[process.env.REGION] || regionalURLs.US) + `t/g/${userId}`;
  const previewLinkUrl = `${linkUrl}?preview=true`;

  const handleOpenDropDown = e => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseDropDown = () => {
    setAnchorEl(null);
  };

  const handleCopyTipLinkClick = () => {
    navigator.clipboard.writeText(linkUrl);
    setShouldShowCopyAlert(true);
    Analytics.track('Users table - copied tip link');
  };

  const handleQRDownloadClick = () => {
    handleQRDownload(linkUrl, profile.firstName, profile.lastName);
    Analytics.track('download guide qr code clicked', {
      page: 'users table - agent summary button'
    });
  };

  const handlePreviewTippingClick = () => {
    Object.assign(document.createElement('a'), {
      target: '_blank',
      rel: 'noopener noreferrer',
      href: previewLinkUrl
    }).click();
    Analytics.track('Users table - opened tipping preview');
  };

  const handleAddToursClick = () => {
    setIsOpen(true);
    Analytics.track('open add tours to guide');
  };

  const handleReviewFirstClick = () => {
    setIsReviewFirstOpen(true);
    handleCloseDropDown();
    Analytics.track('open guide review first setting');
  };

  const handleBusinessCardDownloadClick = () => {
    Analytics.track('download individual business card clicked');
    generateBusinessCardPrintout(profile, process.env.REGION);
  };

  return (
    <div>
      <button
        type="button"
        data-whats-new="guide-actions"
        aria-label="Guide actions"
        className="mr-[5px] inline-flex items-center justify-center bg-white hover:bg-slate-50 text-slate-600 border border-solid border-slate-300 rounded-xl px-3 py-1.5 shadow-sm transition-colors cursor-pointer"
        onClick={handleOpenDropDown}>
        <MoreHorizIcon />
      </button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        className={classes.menu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleCloseDropDown}>
        <MenuItem onClick={handleCopyTipLinkClick}>
          <FormattedMessage {...messages.copyTipLink} />
        </MenuItem>
        <MenuItem onClick={handleQRDownloadClick}>
          <FormattedMessage {...messages.downloadQRCode} />
        </MenuItem>
        <MenuItem onClick={handleBusinessCardDownloadClick}>
          <FormattedMessage {...messages.downloadBusinessCard} />
        </MenuItem>
        <MenuItem onClick={handlePreviewTippingClick}>
          <FormattedMessage {...messages.previewTippingFlow} />
        </MenuItem>
        <MenuItem onClick={handleReviewFirstClick}>
          <FormattedMessage {...messages.reviewFirst} />
        </MenuItem>
        <AccessControlledButton
          elementStyle={classes.menuItem}
          feature={'PostTipReviewCollection'}
          as="MenuItem"
          title={intl.formatMessage(messages.addToursToProfile)}
          onClick={handleAddToursClick}
        />
      </Menu>

      <AddToursModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        activeTours={activeTours}
        profile={profile}
        userId={userId}
        addToursToProfile={tourIds => addToursToProfile(profile.userId, tourIds)}
      />

      <ReviewFirstModal
        isOpen={isReviewFirstOpen}
        setIsOpen={setIsReviewFirstOpen}
        profile={profile}
        companyDirectToPostTip={companyDirectToPostTip}
        setReviewFirst={setGuideReviewFirst}
      />
    </div>
  );
};

export default AgentProfileSummaryButton;
